import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Gift, ScrollText } from "lucide-react";
import api from "@/lib/api";

const useCountUp = (target, run, duration = 1200) => {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!run || target <= 0) { setVal(target); return; }
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, run, duration]);
  return val;
};

const StatItem = ({ icon, value, label, run, testid }) => {
  const n = useCountUp(value, run);
  return (
    <div className="flex flex-col items-center px-4" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="osrs-num osrs-title text-xl sm:text-2xl text-[#FFCF3F]" data-testid={`${testid}-value`}>
          {n.toLocaleString()}
        </span>
      </div>
      <span className="osrs-shadow text-[10px] sm:text-[11px] text-[#b9a06a] uppercase tracking-wide mt-0.5">{label}</span>
    </div>
  );
};

export default function SocialProofStats() {
  const [stats, setStats] = useState(null);
  const [run, setRun] = useState(false);

  useEffect(() => {
    api.get("/public/stats")
      .then(({ data }) => { setStats(data); requestAnimationFrame(() => setRun(true)); })
      .catch(() => {});
  }, []);

  if (!stats) return null;

  // Only render stats above a small threshold (hide zeros so low/empty numbers never hurt trust).
  const items = [
    stats.accounts_verified > 0 && {
      key: "verified",
      icon: <ShieldCheck size={16} className="text-[#7CFC7C]" />,
      value: stats.accounts_verified,
      label: "Accounts verified",
      testid: "stat-verified",
    },
    stats.bonds_paid > 0 && {
      key: "bonds",
      icon: <Gift size={16} className="text-[#FF7A1A]" />,
      value: stats.bonds_paid,
      label: "Bonds paid out",
      testid: "stat-bonds",
    },
    stats.tasks_completed > 0 && {
      key: "tasks",
      icon: <ScrollText size={16} className="text-[#E6A519]" />,
      value: stats.tasks_completed,
      label: "Tasks completed",
      testid: "stat-tasks",
    },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="social-proof-stats"
      className="osrs-panel-plain mt-4 px-3 py-2.5 flex items-stretch justify-center divide-x divide-[#0f0d09]/60"
    >
      {items.map((it) => (
        <StatItem key={it.key} icon={it.icon} value={it.value} label={it.label} run={run} testid={it.testid} />
      ))}
    </div>
  );
}
